import { LocationConfigService } from '@addins/core/core/src/services/location-config/location-config.service';
import { Inject, Injectable } from '@angular/core';
import { Geolocation, Geoposition } from '@awesome-cordova-plugins/geolocation/ngx';
import { Guid } from '@models/guid';
import { TranslatedAlertService } from '@services/translated-alert/translated-alert.service';
import { BackgroundLocation, LocationConfig, LocationState } from '@techwan/ionic-plugin-location';
import { BehaviorSubject, from, fromEvent, Observable, Subscription } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { TOKEN_PROVIDER } from '../../../schema/injection-token';
import { ILocationProvider } from '../../../schema/interfaces/location-provider';
import { TokenProvider } from '../../../schema/interfaces/token-provider';

@Injectable()
export class BackgroundLocationService implements ILocationProvider {
  private _uid: Guid;

  private _subs: Subscription[] = [];

  private _current = new BehaviorSubject<Geoposition>(null);
  get $change(): Observable<Geoposition> {
    return this._current.asObservable();
  }

  constructor(
    private geolocation: Geolocation,
    @Inject(TOKEN_PROVIDER)
    private tokenProvider: TokenProvider,
    private locationConfig: LocationConfigService,
    private translatedAlert: TranslatedAlertService
  ) {}

  getCurrent(): Promise<Geoposition> {
    return this.geolocation.getCurrentPosition({ timeout: 40000, enableHighAccuracy: true });
  }

  setup(uid: Guid) {
    this._uid = uid;
  }

  start(): Observable<any> {
    return this.locationConfig.init().pipe(
      first(),
      map(locationConfig => {
        this.init();
        return from(
          this.configure(locationConfig).then(() =>
            BackgroundLocation.start({ id: this._uid }).catch(exc => {
              if (exc.data?.locationAuthorizationDenied) {
                this.translatedAlert.show({
                  header: 'Mobile.LocationPermissionRequired',
                  message: 'Mobile.AllowLocationPermission',
                  buttons: [{ text: 'Mobile.Ok', value: true }]
                });
              }
              return Promise.reject(exc);
            })
          )
        );
      })
    );
  }

  private init() {
    this._subs.push(this.tokenProvider.$change.subscribe(token => BackgroundLocation.setToken({ token })));
    this._subs.push(
      fromEvent(document, 'newLocation')
        .pipe(map((event: any) => event.detail))
        .subscribe(event => this._current.next(event))
    );
    this._subs.push(this.locationConfig.$change.subscribe(locationConfig => this.configure(locationConfig)));
  }

  private configure(locationConfig: LocationConfig): Promise<any> {
    return BackgroundLocation.configure(locationConfig);
  }

  state(): Observable<LocationState> {
    return from(BackgroundLocation.state()).pipe(map((state: LocationState) => state));
  }

  stop(): Promise<void> {
    while (this._subs.length !== 0) {
      this._subs.pop().unsubscribe();
    }
    this.locationConfig.uninit();
    return BackgroundLocation.stop();
  }
}
